import {z} from 'zod';

import {Chunk} from '../types';
import {extractContent, parsePartialArray} from './parse';

/**
 * Throttle function for rate-limiting function calls.
 *
 * @param func The function to throttle
 * @param delay Minimum milliseconds between calls
 * @returns Throttled version of the function
 */
function throttle<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T {
  let lastCall = 0;
  let timeout: NodeJS.Timeout | null = null;

  return ((...args: Parameters<T>) => {
    const now = Date.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    } else {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(
        () => {
          lastCall = Date.now();
          func(...args);
        },
        delay - (now - lastCall)
      );
    }
  }) as T;
}

/**
 * Combine streaming chunks into a single completion response.
 *
 * @param chunks Array of streaming chunks
 * @returns Combined completion string
 */
export const combineChunks = (chunks: unknown[]): string => {
  if (!Array.isArray(chunks) || chunks.length === 0) {
    return '';
  }

  const contentParts: string[] = [];

  for (const chunk of chunks) {
    const content = extractContent(chunk);
    if (content) {
      contentParts.push(content);
    }
  }

  return contentParts.join('');
};

/**
 * Creates a streaming chunk processor for handling LLM responses.
 *
 * @param onChunk Callback for each processed chunk
 * @param responseFormat Optional response format for special handling
 * @param updateInterval Throttle interval for updates
 * @param signal Optional AbortSignal for cancellation
 * @returns Object containing the processor function and accumulated chunks
 */
export const createChunkProcessor = (
  onChunk: (chunk: Chunk) => void,
  responseFormat?: any,
  updateInterval: number = 150,
  signal?: AbortSignal
) => {
  const processedChunks: unknown[] = [];
  let chunkBuffer = '';
  let processTimeout: NodeJS.Timeout | null = null;
  let accumulatedContent = '';

  // Check if we're dealing with a Zod array schema
  const isZodArraySchema =
    responseFormat && responseFormat instanceof z.ZodArray;

  // Create a throttled update function
  const throttledUpdate = throttle((content: string | any[]) => {
    // Use requestAnimationFrame if available (browser), otherwise direct call
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        onChunk({
          content:
            typeof content === 'string' ? content : JSON.stringify(content),
        });
      });
    } else {
      onChunk({
        content:
          typeof content === 'string' ? content : JSON.stringify(content),
      });
    }
  }, updateInterval);

  // Process accumulated chunks
  const processChunks = () => {
    if (chunkBuffer) {
      accumulatedContent += chunkBuffer;

      if (isZodArraySchema) {
        // For arrays, parse incrementally and send partial arrays
        const partialArray = parsePartialArray(accumulatedContent);
        if (partialArray.length > 0) {
          // Validate items with the schema if provided
          const itemSchema = responseFormat._def.type;
          const validatedItems = partialArray.filter((item: any) => {
            const result = itemSchema.safeParse(item);
            return result.success;
          });

          if (validatedItems.length > 0) {
            throttledUpdate(validatedItems);
          }
        }
      } else {
        // For non-arrays, send raw content
        throttledUpdate(chunkBuffer);
      }
      chunkBuffer = '';
    }
  };

  const processChunk = (rawChunk: unknown) => {
    // Check for cancellation
    if (signal?.aborted) {
      return;
    }

    processedChunks.push(rawChunk);

    const content = extractContent(rawChunk);
    if (content) {
      // Accumulate chunks
      chunkBuffer += content;

      // Schedule processing
      if (processTimeout) {
        clearTimeout(processTimeout);
      }

      // Process chunks with a small delay to batch updates
      processTimeout = setTimeout(processChunks, 10);
    }
  };

  const cleanup = () => {
    if (processTimeout) {
      clearTimeout(processTimeout);
    }
    processChunks();
  };

  return {
    processChunk,
    cleanup,
    getProcessedChunks: () => processedChunks,
  };
};
